import {
  BaseQueryFn,
  MutationDefinition,
  EndpointBuilder,
  QueryDefinition,
} from '@reduxjs/toolkit/query';

import type {
  IGetMagicCredentialsArgs,
  IPostCredentialsArgs,
  TPostCredentialsResponse,
  IPutPasswordChangeArgs,
  IPostRefreshTokenArgs,
  IUserPutArgs,
  IUserPutEmailChangeConfirmArgs,
  IUserPutLocaleArgs,
  IUserPutRequestPasswordMailArgs,
  ITokenResponse,
  IGetLightMagicCredentialsArgs,
  ILightLoginResponse,
} from './user.types';
import type { IUser, IUserMe } from 'interfaces/User.interface';
import { serializeFlatObject } from '../helper/getPaginationParams.helper';

export const postCredentials = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IPostCredentialsArgs,
  BaseQueryFn,
  string,
  TPostCredentialsResponse
> =>
  build.mutation<TPostCredentialsResponse, IPostCredentialsArgs>({
    query: (queryArg) => ({
      url: `api/login_check`,
      method: 'POST',
      body: queryArg.credentials,
    }),
    // Prevents retrying on 400 errors
    extraOptions: { maxRetries: 0, withoutSession: true },
  });

export const putUserRequestPasswordMail = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IUserPutRequestPasswordMailArgs,
  BaseQueryFn,
  string,
  unknown
> =>
  build.mutation<unknown, IUserPutRequestPasswordMailArgs>({
    query: ({ email }) => ({
      url: `api/users/request-password`,
      method: 'PATCH',
      body: {
        email,
      },
    }),
    // Prevents retrying on 400 errors
    extraOptions: { maxRetries: 0, withoutSession: true },
  });

export const postRefreshToken = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IPostRefreshTokenArgs,
  BaseQueryFn,
  string,
  ITokenResponse
> =>
  build.mutation<ITokenResponse, IPostRefreshTokenArgs>({
    query: (queryArg) => ({
      url: `api/token/refresh`,
      method: 'POST',
      body: {
        refresh_token: queryArg.refresh_token,
      },
    }),
    extraOptions: { maxRetries: 0 }, // Prevents retrying on 400 errors
  });

export const getUserMe = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<null, BaseQueryFn, string, IUserMe> =>
  build.query<IUserMe, null>({
    providesTags: ['UserMe'],
    query: () => ({
      url: `api/me`,
      method: 'GET',
    }),
  });

export const getMagicLoginCredentials = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  IGetMagicCredentialsArgs,
  BaseQueryFn,
  string,
  ITokenResponse
> =>
  build.query<ITokenResponse, IGetMagicCredentialsArgs>({
    query: (queryArg: IGetMagicCredentialsArgs) => {
      const credentialsQuery = serializeFlatObject(queryArg.credentials, '?');
      return {
        url: `api/login_link_check${credentialsQuery}`,
        method: 'GET',
      };
    },
    // Prevents retrying on 400 errors
    extraOptions: { maxRetries: 0, withoutSession: true },
  });

export const getLightMagicLoginCredentials = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  IGetLightMagicCredentialsArgs,
  BaseQueryFn,
  string,
  ILightLoginResponse
> =>
  build.query<ILightLoginResponse, IGetLightMagicCredentialsArgs>({
    query: (queryArg: IGetLightMagicCredentialsArgs) => {
      return {
        url: `api/users/${queryArg.user}/request-login-link`,
        method: 'PATCH',
        body: {},
      };
    },
    extraOptions: { maxRetries: 0 }, // Prevents retrying on 400 errors
  });

export const putUserItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<IUserPutArgs, BaseQueryFn, string, IUser> =>
  build.mutation<IUser, IUserPutArgs>({
    invalidatesTags: ['User', 'UserMe'],
    query: (queryArg) => ({
      url: `api/users/${queryArg.id}`,
      method: 'PATCH',
      body: queryArg.body,
    }),
    extraOptions: { maxRetries: 0 }, // Prevents retrying on 400 errors
  });

export const postUserItemLocale = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<IUserPutLocaleArgs, BaseQueryFn, string, IUser> =>
  build.mutation<IUser, IUserPutLocaleArgs>({
    invalidatesTags: ['UserMe'],
    query: (queryArg) => ({
      url: `api/me`,
      method: 'POST',
      body: {
        locale: queryArg.locale,
      },
    }),
    extraOptions: { maxRetries: 0 }, // Prevents retrying on 400 errors
  });

export const putPasswordChange = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<IPutPasswordChangeArgs, BaseQueryFn, string, IUser> =>
  build.mutation<IUser, IPutPasswordChangeArgs>({
    query: (queryArg) => ({
      url: `api/change_password`,
      method: 'PATCH',
      body: {
        oldPassword: queryArg.oldPassword,
        newPassword: queryArg.newPassword,
        newPasswordRetyped: queryArg.newPasswordRetyped,
      },
    }),
    extraOptions: { maxRetries: 0 },
  });

export const putUserEmailChangeConfirm = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IUserPutEmailChangeConfirmArgs,
  BaseQueryFn,
  string,
  IUser
> =>
  build.mutation<IUser, IUserPutEmailChangeConfirmArgs>({
    invalidatesTags: ['User', 'UserMe'],
    query: (queryArg) => ({
      url: `api/users/${queryArg.id}/confirm-email-change`,
      method: 'PATCH',
      body: queryArg.body,
    }),
    extraOptions: { maxRetries: 0 }, // Prevents retrying on 400 errors
  });
